import React from 'react';
import { flushSync } from 'react-dom';
import classNameBind from 'classnames/bind';
import _uniqBy from 'lodash/uniqBy';
import { TFunction } from 'react-i18next';
import { RouterService } from '@piwikpro/router-crate';
import { DebouncedFunc, debounce } from 'lodash';
import {
  OverlayView,
  View,
  Stack,
  Transfer,
  Button,
  Information,
} from '@piwikpro/ui-components';
import {
  ConfigService,
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import TitleComponent from '@piwikpro/title-crate/Title';
import { UserGroup, UserGroups } from '@piwikpro/user-groups-crate';
import { FormValidator } from '@piwikpro/form-crate';
import { UnsavedChangesTracker as UnsavedChangesTrackerComponent } from '@piwikpro/form-crate/components/UnsavedChangesTracker';
import { INonMembersState } from '@piwikpro/user-groups-crate/redux/details/nonMembers';

import styles from './AddUsers.module.css';

const bindClasses = classNameBind.bind(styles);


interface InjectedProps {
  config: ConfigService
  userGroups: UserGroups
  validation: FormValidator
  Title: typeof TitleComponent
  UnsavedChangesTracker: typeof UnsavedChangesTrackerComponent
}

interface Props extends InjectedProps {
  routerService: RouterService
  userGroup: UserGroup
  nonMembers: INonMembersState['list']
  total: number
  isLoading: boolean
  failed: boolean
  t: TFunction
}

interface Element {
  id: string
  name: string
  disabledTooltipContent?: string
  isSelected?: boolean
}

interface State {
  limit: number
  from: {
    offset: number
    elements: Element[]
    search: string
    searchDebounce: DebouncedFunc<any> | null
    checkedElements: Element[]
    total: number
  }
  to: {
    offset: number
    elements: Element[]
    search: string
  }
  formIsLoading: boolean
}

class AddUsers extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      limit: 10,
      from: {
        offset: 0,
        elements: [],
        search: '',
        searchDebounce: null,
        checkedElements: [],
        total: 0,
      },
      to: {
        offset: 0,
        elements: [],
        search: '',
      },
      formIsLoading: false,
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.getListFromBackend = this.getListFromBackend.bind(this);
  }

  componentDidMount() {
    this.getListFromBackend();
  }

  async getListFromBackend() {
    const { from, limit } = this.state;
    const { userGroup, userGroups } = this.props;

    try {
      await userGroups.fetchUsersOutsideUserGroup(userGroup.id, {
        search: from.search,
        offset: from.offset,
        limit,
      });
      // eslint-disable-next-line no-empty
    } catch (err) { }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const { nonMembers, total, t } = this.props;
    const { from, to } = this.state;

    if (prevProps.nonMembers !== nonMembers) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        from: {
          ...from,
          elements: nonMembers.map(nonMember => (
            {
              id: nonMember.id,
              name: nonMember.attributes.email,
              disabledTooltipContent: to.elements.some(toElement => toElement.id === nonMember.id) ? t('user-groups.addUsers.list-element.disabled-tooltip-text') : '',
            }
          )),
          total,
        },
      });
    }
  }

  handleClose() {
    const { routerService } = this.props;

    routerService.dismissAdministrationOverlay();
  }

  async handleSubmit() {
    const {
      userGroup,
      userGroups,
    } = this.props;
    const { to } = this.state;

    this.setState({
      formIsLoading: true,
    });

    try {
      await userGroups.addUsersToUserGroup(userGroup.id, to.elements.map(user => user.id));
      await userGroups.findOne(userGroup.id);

      this.handleClose();
      // eslint-disable-next-line no-empty
    } catch { } finally {
      this.setState({
        formIsLoading: false,
      });
    }
  }

  render() {
    const {
      userGroup,
      isLoading,
      failed,
      validation,
      config,
      t,
      Title,
      UnsavedChangesTracker,
    } = this.props;
    const {
      from, to, limit, formIsLoading,
    } = this.state;

    const elementsToSortedAndFiltered = to.elements
      .filter(el => el.name?.toLowerCase().indexOf(to.search.toLowerCase()) > -1);

    const getVisibleCheckedElementsTo = list => list
      .slice(to.offset, to.offset + limit)
      .filter(el => el.isSelected);

    const areAllVisibleElementsToChecked = getVisibleCheckedElementsTo(elementsToSortedAndFiltered)
      .length === elementsToSortedAndFiltered
      .slice(to.offset, to.offset + limit)
      .filter(el => !el.disabledTooltipContent).length;

    const visibleCheckedElementsFrom = from.elements
      .filter(el => from.checkedElements.findIndex(el2 => el.id === el2.id) > -1);

    const isEveryPossibleElementToSelected = elementsToSortedAndFiltered.length
      && elementsToSortedAndFiltered.length === elementsToSortedAndFiltered
        .filter(el => el.isSelected).length;
    const allFromElementsPossibleToSelectLength = from.total - to.elements.length;

    return (
      <Title
        part={t('user-groups.addUsers.title', { userGroupName: userGroup.attributes.name })}
        subset
      >
        <UnsavedChangesTracker
          discardAction={this.handleClose}
          type="twoOptions"
          isModified={to.elements.length > 0 && !formIsLoading}
          render={(api) => {
            const { UnsavedChangesModal, onClose } = api;
            return (
              <OverlayView
                isOpen
                onClose={onClose}
                title={t('user-groups.addUsers.title', { userGroupName: userGroup.attributes.name })}
              >
                <View heightAuto>
                  <div className={bindClasses('overlay-wrapper')}>
                    <View.Body>
                      <Stack vertical>
                        <Stack.Item fill>
                          <Information type="info">
                            {t('user-groups.addUsers.availableUsers.info')}
                          </Information>
                        </Stack.Item>
                        <Stack.Item fill>
                          {/* this component code is mostly moved and adjusted from component story:
                          https://github.com/PiwikPRO/PPMS-UIComponents/blob/master/storybook/lib/components/TransferDemos/TransferDemoWithBackendPagination/TransferDemoWithBackendPagination.jsx
                        */}
                          <Transfer
                            from={{
                              title: t('user-groups.addUsers.checkboxList.leftHeader'),
                              failed,
                              selected: {
                                checkbox: {
                                  checked: !!visibleCheckedElementsFrom.length,
                                  disabled: from.elements.every(el => el.disabledTooltipContent),
                                  checkedMixed: from.elements
                                    .filter(el => !el.disabledTooltipContent).length
                                    !== visibleCheckedElementsFrom.length,
                                  onChange: ({ target: { checked } }) => {
                                    this.setState({
                                      from: {
                                        ...from,
                                        checkedElements: checked
                                          ? [
                                            ...from.checkedElements,
                                            ...from.elements.filter(
                                              element => !element.disabledTooltipContent,
                                            )]
                                          : from.checkedElements
                                            .filter(
                                              el => !visibleCheckedElementsFrom
                                                .some(
                                                  elCheckedVisible => el.id === elCheckedVisible.id,
                                                ),
                                            ),
                                      },
                                    });
                                  },
                                },
                                hideTotal: from.total <= 0,
                                count: from.checkedElements.length,
                                total: allFromElementsPossibleToSelectLength < 0
                                  ? from.total
                                  : allFromElementsPossibleToSelectLength,
                              },
                              search: {
                                value: from.search,
                                onChange: ({ target: { value } }) => {
                                  const { from: { searchDebounce } } = this.state;
                                  if (searchDebounce) {
                                    searchDebounce.cancel();
                                  }

                                  const handleSearch = debounce(() => {
                                    this.getListFromBackend();
                                  }, config.get('LIST_SEARCH_DELAY'));

                                  this.setState({
                                    from: {
                                      ...from,
                                      searchDebounce: handleSearch,
                                      search: value,
                                      offset: 0,
                                    },
                                  });

                                  handleSearch();
                                },
                              },
                              list: from.elements
                                .map(el => ({
                                  ...el,
                                  isSelected: from.checkedElements
                                    .findIndex(el2 => el.id === el2.id) > -1,
                                })),
                              onItemSelect: (e, id) => {
                                this.setState({
                                  from: {
                                    ...from,
                                    checkedElements: from.checkedElements
                                      .findIndex(el => el.id === id) > -1
                                      ? from.checkedElements.filter(el => el.id !== id)
                                      : [
                                        ...from.checkedElements,
                                        from.elements.find(el => el.id === id)!,
                                      ],
                                  },
                                });
                              },
                              pagination: {
                                onPageChange: (v) => {
                                  flushSync(() => {
                                    this.setState({ from: { ...from, offset: v } });
                                  });
                                  this.getListFromBackend();
                                },
                                total: from.total,
                                startIndex: from.offset,
                                endIndex: from.offset + limit,
                                limitOptions: config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS'),
                              },
                              isLoading,
                            }}
                            to={{
                              title: t('user-groups.addUsers.checkboxList.rightHeader'),
                              selected: {
                                checkbox: {
                                  checked: !!getVisibleCheckedElementsTo(
                                    elementsToSortedAndFiltered,
                                  ).length,
                                  checkedMixed: !areAllVisibleElementsToChecked,
                                  disabled: elementsToSortedAndFiltered.length === 0,
                                  onChange: () => {
                                    const idsToSelect = elementsToSortedAndFiltered
                                      .slice(to.offset, to.offset + limit).map(el => el.id);
                                    this.setState({
                                      to: {
                                        ...to,
                                        elements: to.elements.map(
                                          el => (
                                            idsToSelect.includes(el.id)
                                              && !el.disabledTooltipContent
                                              ? {
                                                ...el,
                                                isSelected: !getVisibleCheckedElementsTo(
                                                  elementsToSortedAndFiltered,
                                                ).length,
                                              }
                                              : el),
                                        ),
                                      },
                                    });
                                  },
                                },
                                hideTotal: elementsToSortedAndFiltered.length === 0,
                                count: to.elements.filter(el => el.isSelected).length,
                                total: elementsToSortedAndFiltered.length,
                              },
                              search: {
                                value: to.search,
                                onChange: ({ target: { value } }) => {
                                  this.setState({
                                    to: { ...to, search: value, offset: 0 },
                                  });
                                },
                              },
                              list: elementsToSortedAndFiltered.slice(to.offset, to.offset + limit),
                              onItemSelect: (e, id) => {
                                this.setState({
                                  to: {
                                    ...to,
                                    elements: to.elements
                                      .map(el => (el.id === id ? {
                                        ...el,
                                        isSelected: !el.isSelected,
                                      } : el)),
                                  },
                                });
                              },
                              pagination: {
                                onPageChange: (v) => {
                                  this.setState({ to: { ...to, offset: v } });
                                },
                                total: elementsToSortedAndFiltered.length,
                                startIndex: to.offset,
                                endIndex: to.offset + limit,
                                limitOptions: config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS'),
                              },
                              onSelectAll: elementsToSortedAndFiltered.length > 0 ? () => {
                                const idsToSelect = elementsToSortedAndFiltered.map(el => el.id);
                                this.setState({
                                  to: {
                                    ...to,
                                    elements: to.elements.map(
                                      el => (
                                        idsToSelect.includes(el.id)
                                          ? {
                                            ...el,
                                            isSelected: !isEveryPossibleElementToSelected,
                                          } : el),
                                    ),
                                  },
                                });
                              } : undefined,
                              deselectAllButtonMode: Boolean(isEveryPossibleElementToSelected),
                            }}
                            limit={limit}
                            onLimitChange={(v) => {
                              flushSync(() => {
                                this.setState({
                                  limit: v,
                                  from: { ...from, offset: 0 },
                                  to: { ...to, offset: 0 },
                                });
                              });
                              this.getListFromBackend();
                            }}
                            onMoveItemToRight={
                              (draggedEl) => {
                                this.setState(({ to: latestTo, from: latestFrom }) => ({
                                  to: {
                                    ...latestTo,
                                    elements: _uniqBy([
                                      ...latestTo.elements,
                                      ...latestFrom.checkedElements,
                                      ...draggedEl.id ? [draggedEl] : [],
                                    ], 'id'),
                                  },
                                  from: {
                                    ...latestFrom,
                                    checkedElements: [],
                                  },
                                }));
                                this.getListFromBackend();
                              }
                            }
                            onMoveItemToLeft={
                              (draggedEl) => {
                                this.setState(({ to: latestTo }) => {
                                  const filteredItems = latestTo.elements
                                    .filter(el => !el.isSelected && el.id !== draggedEl.id);
                                  const currentPage = latestTo.offset;
                                  const lastPage = filteredItems.length > limit
                                    ? (Math.ceil(filteredItems.length / limit) - 1) * limit
                                    : 0;

                                  return {
                                    to: {
                                      ...latestTo,
                                      elements: filteredItems,
                                      offset: currentPage > lastPage ? lastPage : currentPage,
                                    },
                                  };
                                });
                                this.getListFromBackend();
                              }
                            }
                          />
                        </Stack.Item>
                      </Stack>
                    </View.Body>
                  </div>
                  <View.Action isFloating>
                    <Stack horizontalAlignment="right" fullWidth spacing="narrow">
                      <Stack.Item>
                        <Button onClick={onClose} text={t('components:cancel')} disabled={formIsLoading} />
                      </Stack.Item>
                      <Stack.Item>
                        <Button
                          appearance="primary"
                          text={t('user-groups.addUsers.button.submit')}
                          disabled={!validation.isValueValid(to.elements, 'userGroups.users')}
                          pending={formIsLoading}
                          onClick={this.handleSubmit}
                        />
                      </Stack.Item>
                      <UnsavedChangesModal />
                    </Stack>
                  </View.Action>
                </View>
              </OverlayView>
            );
          }}
        />
      </Title>
    );
  }
}

export default withInject({
  config: 'config',
  userGroups: 'UserGroupsCrate.userGroups',
  validation: 'FormCrate.validation',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
  UnsavedChangesTracker: 'FormCrate.components.UnsavedChangesTracker',
})(
  errorBoundary(AddUsers),
);
